import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, AlertCircle, Loader2, Phone, RotateCcw, MessageSquare, Calendar as CalIcon, ChevronRight, ExternalLink, Copy, Send, UserPlus2 } from "lucide-react";
import { toast } from "sonner";

const FU_STATUSES = ["Contacted", "Demoed", "Subscribed"];
const FU_DAYS = [3, 7, 14];
const FU_ACTIONS = [
  { id: "done", label: "After Done", desc: "Sent right after the CCO marks the follow-up call as completed" },
  { id: "reschedule", label: "After Reschedule", desc: "Sent when the CCO postpones the follow-up to a new date" },
];

const FU_PLACEHOLDERS = [
  { key: "{workshop_name}", sample: "AutoCare Sdn Bhd" },
  { key: "{next_followup_date}", sample: "12 June 2026" },
  { key: "{day}", sample: "3" },
  { key: "{status}", sample: "Contacted" },
];

const MEETING_PLACEHOLDERS = [
  { key: "{workshop_name}", sample: "AutoCare Sdn Bhd" },
  { key: "{meeting_date}", sample: "28 April 2026" },
  { key: "{meeting_time}", sample: "02:30 PM" },
  { key: "{join_url}", sample: "https://teams.microsoft.com/l/meetup-join/..." },
];

const REGISTRATION_PLACEHOLDERS = [
  { key: "{workshop_name}", sample: "AutoCare Sdn Bhd" },
];

const UNSUBSCRIBED_PLACEHOLDERS = [
  { key: "{workshop_name}", sample: "AutoCare Sdn Bhd" },
  { key: "{previous_plan}", sample: "Plus" },
];

const SUBSCRIBED_PLACEHOLDERS = [
  { key: "{workshop_name}", sample: "AutoCare Sdn Bhd" },
  { key: "{plan}", sample: "Plus" },
];

export function WhatsAppIntegrationSetup({ integration, mode = "settings" }) {
  const API = process.env.REACT_APP_BACKEND_URL + "/api";
  const isTemplates = mode === "templates";
  const defaultTab = isTemplates ? "registration" : "setup";
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [templates, setTemplates] = useState({ en: {}, ms: {} });
  const [editLang, setEditLang] = useState("en");
  const [autoSend, setAutoSend] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Registration & Unsubscribed templates
  const [regTemplates, setRegTemplates] = useState({ en: "", ms: "" });
  const [regDefaults, setRegDefaults] = useState({ en: "", ms: "" });
  const [unsubTemplates, setUnsubTemplates] = useState({ en: "", ms: "" });
  const [unsubDefaults, setUnsubDefaults] = useState({ en: "", ms: "" });
  // Subscribed templates (auto-send on paid plan subscription)
  const [subTemplates, setSubTemplates] = useState({ en: "", ms: "" });
  const [subDefaults, setSubDefaults] = useState({ en: "", ms: "" });
  // Meeting template defaults (used when no saved template exists yet)
  const [meetingDefaults, setMeetingDefaults] = useState({ en: {}, ms: {} });
  // Follow-up WA templates state
  const [fuTemplates, setFuTemplates] = useState({ en: {}, ms: {} });
  const [fuStatus, setFuStatus] = useState("Contacted");
  const [fuLang, setFuLang] = useState("en");
  const [fuDay, setFuDay] = useState(3);
  const [fuAction, setFuAction] = useState("done");
  const [fuDefaults, setFuDefaults] = useState({ en: {}, ms: {} });
  // Sender number state
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [savingSender, setSavingSender] = useState(false);
  // Preview state
  const [previewMode, setPreviewMode] = useState("followup"); // "meeting" | "followup"
  // Textarea ref for placeholder insertion
  const editorRef = useRef(null);
  // Send-Preview-to-my-number dialog state
  const [sendPreviewOpen, setSendPreviewOpen] = useState(false);
  const [sendPreviewPhone, setSendPreviewPhone] = useState("");
  const [sendingPreview, setSendingPreview] = useState(false);
  const [sendPreviewSource, setSendPreviewSource] = useState("followup"); // which template to send

  // Load current user phone for default preview recipient
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me`);
        if (r.data?.phone) setSendPreviewPhone(r.data.phone);
        else if (r.data?.whatsapp) setSendPreviewPhone(r.data.whatsapp);
      } catch (e) { /* ignore */ }
    })();
  }, [API]);

  const handleSendPreview = async () => {
    if (!sendPreviewPhone.trim()) {
      toast.error("Enter your WhatsApp number (with country code, e.g. +60111234567)");
      return;
    }
    setSendingPreview(true);
    try {
      const payload = {
        phone: sendPreviewPhone.trim(),
        language: editLang,
        mode: sendPreviewSource,
      };
      if (sendPreviewSource === "followup") {
        payload.status = fuStatus;
        payload.day = fuDay;
        payload.action = fuAction;
        payload.message = getFuValue(fuStatus, fuDay, fuAction);
      } else if (sendPreviewSource === "registration") {
        payload.message = getRegValue(editLang);
      } else if (sendPreviewSource === "unsubscribed") {
        payload.message = getUnsubValue(editLang);
      } else if (sendPreviewSource === "subscribed") {
        payload.message = getSubValue(editLang);
        payload.plan = "Plus";
      } else {
        // meeting
        payload.message = [
          tpl.greeting || "", tpl.body || "", tpl.meeting_link || "", tpl.closing || "", tpl.sign_off || ""
        ].filter(Boolean).join("\n\n");
      }
      const r = await axios.post(`${API}/integrations/whatsapp-config/send-preview`, payload);
      if (r.data?.success) {
        toast.success(`Preview sent to ${r.data.phone}!`);
        setSendPreviewOpen(false);
      } else {
        toast.error("Preview send did not complete");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send preview");
    }
    setSendingPreview(false);
  };


  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/integrations/whatsapp-config`);
        // Load defaults first (used as fallback below)
        let meetDef = { en: {}, ms: {} };
        try {
          const defRes = await axios.get(`${API}/integrations/whatsapp-config/followup-templates-defaults`);
          if (defRes.data?.defaults) setFuDefaults(defRes.data.defaults);
          const regRes = await axios.get(`${API}/integrations/whatsapp-config/registration-templates-defaults`);
          if (regRes.data?.defaults) setRegDefaults(regRes.data.defaults);
          const unsubRes = await axios.get(`${API}/integrations/whatsapp-config/unsubscribed-templates-defaults`);
          if (unsubRes.data?.defaults) setUnsubDefaults(unsubRes.data.defaults);
          const subRes = await axios.get(`${API}/integrations/whatsapp-config/subscribed-templates-defaults`);
          if (subRes.data?.defaults) setSubDefaults(subRes.data.defaults);
          const meetRes = await axios.get(`${API}/integrations/whatsapp-config/meeting-templates-defaults`);
          if (meetRes.data?.defaults) { meetDef = meetRes.data.defaults; setMeetingDefaults(meetDef); }
        } catch (e) { console.error(e); }

        const stored = res.data?.whatsapp_templates || {};
        const hasStoredMeeting = (l) => !!(stored[l] && stored[l].greeting);
        setTemplates({
          en: hasStoredMeeting("en") ? stored.en : (meetDef.en || {}),
          ms: hasStoredMeeting("ms") ? stored.ms : (meetDef.ms || {}),
        });
        if (res.data?.auto_send_enabled) setAutoSend(res.data.auto_send_enabled);
        if (res.data?.followup_wa_templates) setFuTemplates(res.data.followup_wa_templates);
        if (res.data?.registration_wa_templates) setRegTemplates(res.data.registration_wa_templates);
        if (res.data?.unsubscribed_wa_templates) setUnsubTemplates(res.data.unsubscribed_wa_templates);
        if (res.data?.subscribed_wa_templates) setSubTemplates(res.data.subscribed_wa_templates);
        const creds = res.data?.credentials || {};
        setPhoneNumberId(creds.phone_number_id || "");
        setDisplayPhoneNumber(creds.display_phone_number || "");
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchConfig();
  }, [API]);

  const handleSaveTemplates = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/integrations/whatsapp-config/templates`, { whatsapp_templates: templates, auto_send_enabled: autoSend });
      toast.success("WhatsApp templates saved!");
    } catch { toast.error("Failed to save templates"); }
    setSaving(false);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/integrations/whatsapp-config/templates`, { whatsapp_templates: templates, auto_send_enabled: autoSend });
      toast.success("Settings saved!");
    } catch { toast.error("Failed to save settings"); }
    setSaving(false);
  };

  const handleSaveFollowupTemplates = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/integrations/whatsapp-config/followup-templates`, { followup_wa_templates: fuTemplates });
      toast.success("Follow-up templates saved!");
    } catch { toast.error("Failed to save follow-up templates"); }
    setSaving(false);
  };

  const handleSaveRegistration = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/integrations/whatsapp-config/registration-templates`, { registration_wa_templates: regTemplates });
      toast.success("Registration templates saved!");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const handleSaveUnsubscribed = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/integrations/whatsapp-config/unsubscribed-templates`, { unsubscribed_wa_templates: unsubTemplates });
      toast.success("Unsubscribed templates saved!");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const handleSaveSubscribed = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/integrations/whatsapp-config/subscribed-templates`, { subscribed_wa_templates: subTemplates });
      toast.success("Subscribed templates saved!");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const getRegValue = (lang) => (regTemplates[lang] ?? regDefaults[lang] ?? "");
  const getUnsubValue = (lang) => (unsubTemplates[lang] ?? unsubDefaults[lang] ?? "");
  const getSubValue = (lang) => (subTemplates[lang] ?? subDefaults[lang] ?? "");
  const handleResetReg = (lang) => setRegTemplates(prev => ({ ...prev, [lang]: regDefaults[lang] || "" }));
  const handleResetUnsub = (lang) => setUnsubTemplates(prev => ({ ...prev, [lang]: unsubDefaults[lang] || "" }));
  const handleResetSub = (lang) => setSubTemplates(prev => ({ ...prev, [lang]: subDefaults[lang] || "" }));

  const handleResetFollowupTemplate = (status, day, action) => {
    const def = ((fuDefaults[fuLang] || {})[status] || {})[String(day)] || {};
    setFuTemplates((prev) => ({
      ...prev,
      [fuLang]: {
        ...(prev[fuLang] || {}),
        [status]: {
          ...((prev[fuLang] || {})[status] || {}),
          [String(day)]: {
            ...(((prev[fuLang] || {})[status] || {})[String(day)] || {}),
            [action]: def[action] || "",
          },
        },
      },
    }));
  };

  const updateFollowupField = (status, day, action, value) => {
    setFuTemplates((prev) => ({
      ...prev,
      [fuLang]: {
        ...(prev[fuLang] || {}),
        [status]: {
          ...((prev[fuLang] || {})[status] || {}),
          [String(day)]: {
            ...(((prev[fuLang] || {})[status] || {})[String(day)] || {}),
            [action]: value,
          },
        },
      },
    }));
  };

  const getFuValue = (status, day, action) => {
    const stored = (((fuTemplates[fuLang] || {})[status] || {})[String(day)] || {})[action];
    if (stored !== undefined && stored !== null) return stored;
    return (((fuDefaults[fuLang] || {})[status] || {})[String(day)] || {})[action] || "";
  };

  const handleSaveSender = async () => {
    if (!phoneNumberId.trim()) {
      toast.error("Phone Number ID is required");
      return;
    }
    setSavingSender(true);
    try {
      await axios.put(`${API}/integrations/whatsapp-config/sender`, {
        phone_number_id: phoneNumberId.trim(),
        access_token: accessToken.trim(),
        display_phone_number: displayPhoneNumber.trim(),
      });
      toast.success("Sending number updated!");
      setAccessToken("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update sender");
    }
    setSavingSender(false);
  };

  const tpl = templates[editLang] || {};
  const updateField = (field, value) => {
    setTemplates(prev => ({ ...prev, [editLang]: { ...prev[editLang], [field]: value } }));
  };

  // Insert a placeholder at current cursor position in the active editor textarea
  const insertPlaceholder = (placeholder) => {
    const el = editorRef.current;
    if (!el) return;
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const current = getFuValue(fuStatus, fuDay, fuAction);
    const next = current.slice(0, start) + placeholder + current.slice(end);
    updateFollowupField(fuStatus, fuDay, fuAction, next);
    // Restore cursor position after insert
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + placeholder.length, start + placeholder.length);
    }, 10);
  };

  // Render a template string with sample values (for live preview)
  const renderSample = (text, kind = "followup") => {
    if (!text) return "";
    const list = kind === "meeting" ? MEETING_PLACEHOLDERS
      : kind === "registration" ? REGISTRATION_PLACEHOLDERS
      : kind === "unsubscribed" ? UNSUBSCRIBED_PLACEHOLDERS
      : kind === "subscribed" ? SUBSCRIBED_PLACEHOLDERS
      : FU_PLACEHOLDERS;
    let out = text;
    list.forEach(p => {
      out = out.split(p.key).join(p.sample);
    });
    return out;
  };

  // Format text with WhatsApp markdown — *bold* → <strong>, _italic_ → <em>
  const formatWhatsAppText = (text) => {
    if (!text) return null;
    // Escape HTML
    const esc = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const formatted = esc
      .replace(/\*([^*\n]+)\*/g, "<strong>$1</strong>")
      .replace(/_([^_\n]+)_/g, "<em>$1</em>")
      .replace(/~([^~\n]+)~/g, "<del>$1</del>")
      .replace(/\n/g, "<br/>");
    return <span dangerouslySetInnerHTML={{ __html: formatted }} />;
  };

  const copyToClipboard = async (text, label = "Copied!") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  // Build full meeting preview message
  const previewMsg = [
    tpl.greeting || "", tpl.body || "", tpl.meeting_link || "", tpl.closing || "", tpl.sign_off || ""
  ].filter(Boolean).join("\n\n")
    .replace(/\{lead_name\}/g, "Ahmad")
    .replace(/\{company\}/g, "AutoCare Sdn Bhd")
    .replace(/\{meeting_date\}/g, "28 April 2026")
    .replace(/\{meeting_time\}/g, "02:30 PM")
    .replace(/\{join_url\}/g, "https://teams.microsoft.com/l/meetup-join/...");

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#25D366]" /></div>;

  return (
    <div className="space-y-4" data-testid="whatsapp-setup">
      <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg flex-wrap">
        {(isTemplates
          ? [{id:"registration",label:"Registration"},{id:"templates",label:"Meeting"},{id:"followups",label:"Follow-up"},{id:"subscribed",label:"Subscribed"},{id:"unsubscribed",label:"Unsubscribed"},{id:"preview",label:"Preview"}]
          : [{id:"setup",label:"Setup Guide"},{id:"sender",label:"Sending Number"}]
        ).map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} className={`flex-1 py-1.5 px-3 text-xs font-medium rounded-md transition-colors min-w-[100px] ${activeTab === t.id ? "bg-white text-[#25D366] shadow-sm" : "text-gray-500 hover:text-gray-700"}`} data-testid={`wa-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "setup" && (
        <div className="space-y-4" data-testid="wa-setup-tab">
          {/* Status banner */}
          <div className={`p-3 rounded-xl border flex items-center justify-between ${integration.status === "connected" ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
            <div className="flex items-center gap-2">
              {integration.status === "connected" ? (
                <><CheckCircle2 className="w-5 h-5 text-emerald-600" /><div><p className="text-sm font-semibold text-emerald-900">WhatsApp Connected</p><p className="text-xs text-emerald-700">{integration.credentials?.display_phone_number || "Number configured"}</p></div></>
              ) : (
                <><AlertCircle className="w-5 h-5 text-amber-600" /><div><p className="text-sm font-semibold text-amber-900">Not yet connected</p><p className="text-xs text-amber-700">Follow the steps below to connect your WhatsApp Business number</p></div></>
              )}
            </div>
            {integration.status !== "connected" && (
              <Button size="sm" onClick={() => setActiveTab("sender")} className="bg-[#25D366] hover:bg-[#1da851] text-white rounded-lg text-xs" data-testid="wa-jump-to-sender">
                Go to credentials <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>

          {/* 4-step wizard */}
          <div className="space-y-3">
            {[
              {
                num: 1,
                title: "Create a Meta Business account",
                desc: "Sign in to Meta Business Suite using the Facebook account that owns your business. Skip if you already have one.",
                cta: { label: "Open Meta Business Suite", href: "https://business.facebook.com" },
                hint: "Tip: Use the email you use to manage your business Facebook page.",
              },
              {
                num: 2,
                title: "Create a new app on Meta for Developers",
                desc: "Go to developers.facebook.com → My Apps → Create App → choose Business type → add WhatsApp product.",
                cta: { label: "Open Meta for Developers", href: "https://developers.facebook.com/apps" },
                hint: "Once added, you'll land on the WhatsApp → API Setup page automatically.",
              },
              {
                num: 3,
                title: "Add your WhatsApp Business phone number",
                desc: "On API Setup, click 'Add phone number'. You'll be asked to verify it via SMS or call. Once verified, Meta will assign you a unique Phone Number ID.",
                cta: null,
                hint: "Test numbers can be used immediately for development. For production, the number must be verified.",
              },
              {
                num: 4,
                title: "Copy your Phone Number ID and Access Token",
                desc: "From the API Setup page, copy the Phone Number ID (under 'From'). Generate a Permanent Access Token via System Users in Business Settings → System Users → Generate New Token.",
                cta: null,
                hint: "Paste both into the 'Sending Number' tab next. The access token is stored encrypted.",
              },
              {
                num: 5,
                title: "Customize messages and turn on auto-send",
                desc: "Use the Templates tab (top of this panel, next to Webhook) to edit Meeting + Follow-up messages. Then toggle Auto-Send below.",
                cta: null,
                hint: "All templates support English & Bahasa Melayu, switched based on each lead's preferred language.",
              },
            ].map((step) => (
              <div key={step.num} className="p-4 border border-gray-200 rounded-xl bg-white hover:border-[#25D366]/30 hover:shadow-sm transition-all" data-testid={`wa-setup-step-${step.num}`}>
                <div className="flex gap-3">
                  <div className="shrink-0 w-8 h-8 rounded-full bg-[#25D366] text-white text-sm font-bold flex items-center justify-center">{step.num}</div>
                  <div className="flex-1 min-w-0">
                    <h5 className="text-sm font-semibold text-gray-900">{step.title}</h5>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">{step.desc}</p>
                    {step.hint && (
                      <p className="text-[11px] text-gray-400 mt-2 flex items-start gap-1">
                        <span className="text-[#25D366]">›</span>
                        <span>{step.hint}</span>
                      </p>
                    )}
                    {step.cta && (
                      step.cta.href ? (
                        <a href={step.cta.href} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#25D366] hover:text-[#1da851]" data-testid={`wa-setup-step-${step.num}-cta`}>
                          {step.cta.label} <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <button onClick={step.cta.action} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#25D366] hover:text-[#1da851]" data-testid={`wa-setup-step-${step.num}-cta`}>
                          {step.cta.label} <ChevronRight className="w-3 h-3" />
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Auto-Send Toggle */}
          <div className="p-4 bg-white rounded-xl border border-gray-200">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h5 className="text-sm font-semibold text-gray-900">Auto-Send Messages</h5>
                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">Automatically send a WhatsApp message when a CCO schedules a demo, or completes / reschedules a follow-up call.</p>
              </div>
              <button
                onClick={() => { setAutoSend(!autoSend); }}
                className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${autoSend ? "bg-[#25D366]" : "bg-gray-300"}`}
                data-testid="wa-auto-send-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${autoSend ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
            {autoSend && integration.status !== "connected" && (
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Connect a WhatsApp number first (Sending Number tab)</p>
            )}
          </div>

          <Button onClick={handleSaveSettings} disabled={saving} className="w-full bg-[#25D366] hover:bg-[#1da851] text-white rounded-lg" data-testid="wa-save-setup-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Save Settings
          </Button>
        </div>
      )}


      {activeTab === "sender" && (
        <div className="space-y-4" data-testid="wa-sender-tab">
          <div className="p-4 bg-[#25D366]/5 border border-[#25D366]/15 rounded-xl space-y-3">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#25D366]" />
              <h4 className="font-semibold text-sm text-gray-900">WhatsApp Sending Number</h4>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Configure which WhatsApp Business number messages will be sent from. The <strong>Phone Number ID</strong> identifies the number registered to your Meta Business account.
            </p>

            <div className="grid gap-3 mt-2">
              <div>
                <Label className="text-xs text-gray-500">Display Number (for reference)</Label>
                <Input
                  value={displayPhoneNumber}
                  onChange={(e) => setDisplayPhoneNumber(e.target.value)}
                  placeholder="+60 11-1234 5678"
                  className="border-gray-300 rounded-lg text-sm"
                  data-testid="wa-sender-display"
                />
                <p className="text-[10px] text-gray-400 mt-0.5">The actual phone number shown to recipients (informational only)</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Phone Number ID *</Label>
                <Input
                  value={phoneNumberId}
                  onChange={(e) => setPhoneNumberId(e.target.value)}
                  placeholder="e.g. 123456789012345"
                  className="border-gray-300 rounded-lg text-sm font-mono"
                  data-testid="wa-sender-phone-id"
                />
                <p className="text-[10px] text-gray-400 mt-0.5">From Meta for Developers → WhatsApp → API Setup → Phone Number ID</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Permanent Access Token (leave blank to keep current)</Label>
                <Input
                  type="password"
                  value={accessToken}
                  onChange={(e) => setAccessToken(e.target.value)}
                  placeholder="EAA... (generated via System User)"
                  className="border-gray-300 rounded-lg text-sm font-mono"
                  data-testid="wa-sender-token"
                />
                <p className="text-[10px] text-gray-400 mt-0.5">Existing token will be preserved if left empty</p>
              </div>
            </div>

            <Button onClick={handleSaveSender} disabled={savingSender || !phoneNumberId.trim()} className="w-full bg-[#25D366] hover:bg-[#1da851] text-white rounded-lg" data-testid="wa-save-sender-btn">
              {savingSender ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Save Sending Number
            </Button>
          </div>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>To send from a different WhatsApp number, register it in your Meta Business account, copy its <strong>Phone Number ID</strong>, then paste it above.</span>
          </div>
        </div>
      )}


      {(activeTab === "registration" || activeTab === "unsubscribed" || activeTab === "subscribed") && (() => {
        const isReg = activeTab === "registration";
        const isSub = activeTab === "subscribed";
        const placeholders = isReg ? REGISTRATION_PLACEHOLDERS : isSub ? SUBSCRIBED_PLACEHOLDERS : UNSUBSCRIBED_PLACEHOLDERS;
        const valEn = isReg ? getRegValue("en") : isSub ? getSubValue("en") : getUnsubValue("en");
        const valMs = isReg ? getRegValue("ms") : isSub ? getSubValue("ms") : getUnsubValue("ms");
        const setVal = (lang, v) => {
          if (isReg) setRegTemplates(prev => ({ ...prev, [lang]: v }));
          else if (isSub) setSubTemplates(prev => ({ ...prev, [lang]: v }));
          else setUnsubTemplates(prev => ({ ...prev, [lang]: v }));
        };
        const handleReset = (lang) => isReg ? handleResetReg(lang) : isSub ? handleResetSub(lang) : handleResetUnsub(lang);
        const handleSave = isReg ? handleSaveRegistration : isSub ? handleSaveSubscribed : handleSaveUnsubscribed;
        const title = isReg ? "Workshop Registration" : isSub ? "Subscribed (Paid Plan)" : "Unsubscribed (Paid → Free)";
        const desc = isReg
          ? "Auto-sent the moment ENGARAGE creates a new workshop registration."
          : isSub
          ? "Auto-sent the moment a workshop successfully subscribes to a paid plan (Basic / Plus / Pro)."
          : "Auto-sent when a workshop downgrades from a paid plan (Basic/Plus/Pro) to Free.";
        const saveLabel = isReg ? "Registration" : isSub ? "Subscribed" : "Unsubscribed";
        const renderEditor = (lang, value) => (
          <div className="space-y-2" data-testid={`${activeTab}-editor-${lang}`}>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-gray-700 font-semibold">{lang === "en" ? "English" : "Bahasa Melayu"}</Label>
              <button onClick={() => handleReset(lang)} className="inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-[#25D366]" data-testid={`${activeTab}-reset-${lang}`}>
                <RotateCcw className="w-3 h-3" /> Reset
              </button>
            </div>
            <textarea
              value={value}
              onChange={(e) => setVal(lang, e.target.value)}
              rows={9}
              className="w-full border border-gray-300 rounded-lg text-sm p-3 resize-none focus:ring-1 focus:ring-[#25D366] focus:border-[#25D366] outline-none bg-white font-mono leading-relaxed"
              data-testid={`${activeTab}-tpl-${lang}`}
            />
            <div className="flex items-center justify-between text-[10px] text-gray-400">
              <span>{value.length} chars</span>
              <span>Use <code className="bg-gray-100 px-1 rounded">*bold*</code> · <code className="bg-gray-100 px-1 rounded">_italic_</code></span>
            </div>
          </div>
        );
        return (
          <div className="space-y-4" data-testid={`wa-${activeTab}-tab`}>
            <div className="p-3 rounded-lg border border-[#25D366]/20 bg-[#25D366]/5 flex items-start gap-2">
              <MessageSquare className="w-4 h-4 text-[#25D366] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-gray-900">{title}</p>
                <p className="text-xs text-gray-600 mt-0.5">{desc}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {renderEditor("en", valEn)}
              {renderEditor("ms", valMs)}
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">Available variables</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {placeholders.map((p) => (
                  <span key={p.key} className="px-2 py-1 text-[11px] font-mono bg-[#25D366]/8 text-[#0D0D5B] border border-[#25D366]/20 rounded-md" title={`Sample: ${p.sample}`}>
                    {p.key}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#25D366] hover:bg-[#1da851] text-white rounded-lg" data-testid={`wa-save-${activeTab}-btn`}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Save {saveLabel} Templates
              </Button>
              <Button onClick={() => { setSendPreviewSource(activeTab); setSendPreviewOpen(true); }} variant="outline" className="border-[#25D366] text-[#0D0D5B] hover:bg-[#25D366]/10 rounded-lg" data-testid={`wa-send-preview-${activeTab}-btn`}>
                <Send className="w-4 h-4 mr-2" /> Send Preview
              </Button>
            </div>
          </div>
        );
      })()}


      {activeTab === "followups" && (
        <div className="space-y-4" data-testid="wa-followups-tab">
          {/* Top toolbar — language + status pills */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <MessageSquare className="w-3.5 h-3.5 text-[#25D366]" />
              <span>Sent automatically after each follow-up conversation with the lead.</span>
            </div>
            <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg">
              {[{id:"en",label:"EN"},{id:"ms",label:"BM"}].map(l => (
                <button key={l.id} onClick={() => setFuLang(l.id)} className={`py-1 px-3 text-xs font-semibold rounded-md transition-colors ${fuLang === l.id ? "bg-white text-[#25D366] shadow-sm" : "text-gray-500 hover:text-gray-700"}`} data-testid={`fu-lang-${l.id}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          {/* Status pills */}
          <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
            {FU_STATUSES.map(s => (
              <button key={s} onClick={() => setFuStatus(s)} className={`flex-1 py-2 px-3 text-xs font-medium rounded-md transition-colors ${fuStatus === s ? "bg-white text-[#0D0D5B] shadow-sm" : "text-gray-500 hover:text-gray-700"}`} data-testid={`fu-status-${s}`}>
                {s}
              </button>
            ))}
          </div>

          {/* Day + Action selector chips */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] text-gray-500 uppercase tracking-wide font-semibold">Follow-up Day</Label>
              <div className="flex gap-1.5 mt-1.5">
                {FU_DAYS.map(d => (
                  <button key={d} onClick={() => setFuDay(d)} className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border transition-all ${fuDay === d ? "bg-[#25D366] text-white border-[#25D366] shadow-sm" : "bg-white text-gray-500 border-gray-300 hover:border-[#25D366]/50"}`} data-testid={`fu-day-${d}`}>
                    +{d} day
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[11px] text-gray-500 uppercase tracking-wide font-semibold">Trigger Action</Label>
              <div className="flex gap-1.5 mt-1.5">
                {FU_ACTIONS.map(a => (
                  <button key={a.id} onClick={() => setFuAction(a.id)} className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border transition-all ${fuAction === a.id ? "bg-[#25D366] text-white border-[#25D366] shadow-sm" : "bg-white text-gray-500 border-gray-300 hover:border-[#25D366]/50"}`} data-testid={`fu-action-${a.id}`}>
                    {a.label.replace("After ","")}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Two-column: Editor + Live Preview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* LEFT: editor */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-gray-700 font-semibold">
                  Message for <span className="text-[#25D366]">{fuStatus}</span> · +{fuDay}d · <span className="capitalize">{fuAction}</span>
                </Label>
                <div className="flex gap-2">
                  <button onClick={() => handleResetFollowupTemplate(fuStatus, fuDay, fuAction)} className="inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-[#25D366]" data-testid={`fu-reset-current`}>
                    <RotateCcw className="w-3 h-3" /> Reset
                  </button>
                  <button onClick={() => copyToClipboard(getFuValue(fuStatus, fuDay, fuAction), "Template copied")} className="inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-[#25D366]" data-testid={`fu-copy-current`}>
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
              </div>
              <p className="text-[10.5px] text-gray-400 leading-snug">{FU_ACTIONS.find(a => a.id === fuAction)?.desc}</p>

              <textarea
                ref={editorRef}
                value={getFuValue(fuStatus, fuDay, fuAction)}
                onChange={(e) => updateFollowupField(fuStatus, fuDay, fuAction, e.target.value)}
                rows={10}
                className="w-full border border-gray-300 rounded-lg text-sm p-3 resize-none focus:ring-1 focus:ring-[#25D366] focus:border-[#25D366] outline-none bg-white font-mono leading-relaxed"
                data-testid={`fu-tpl-editor`}
              />
              <div className="flex items-center justify-between text-[10px] text-gray-400">
                <span>{getFuValue(fuStatus, fuDay, fuAction).length} characters · WhatsApp limit ≈ 4096</span>
                <span>Use <code className="bg-gray-100 px-1 rounded">*bold*</code> · <code className="bg-gray-100 px-1 rounded">_italic_</code></span>
              </div>

              {/* Placeholder chips — click to insert */}
              <div>
                <Label className="text-[11px] text-gray-500">Insert variables</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {FU_PLACEHOLDERS.map((p) => (
                    <button
                      key={p.key}
                      onClick={() => insertPlaceholder(p.key)}
                      className="px-2 py-1 text-[11px] font-mono bg-[#25D366]/8 text-[#0D0D5B] border border-[#25D366]/20 rounded-md hover:bg-[#25D366]/15 transition-colors"
                      data-testid={`fu-placeholder-${p.key.replace(/[{}]/g, '')}`}
                      title={`Sample: ${p.sample}`}
                    >
                      {p.key}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT: WhatsApp chat-style preview */}
            <div>
              <Label className="text-xs text-gray-700 font-semibold flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live Preview
              </Label>
              <div className="mt-1 bg-[#E5DDD5] rounded-xl p-3 min-h-[280px] relative overflow-hidden" data-testid="fu-preview">
                {/* faux WhatsApp pattern */}
                <div className="absolute inset-0 opacity-30 pointer-events-none" style={{backgroundImage:'radial-gradient(circle, #c5b9a8 1px, transparent 1px)', backgroundSize:'16px 16px'}}/>
                <div className="relative max-w-[92%] ml-auto">
                  <div className="bg-[#DCF8C6] rounded-lg rounded-tr-none p-3 shadow-sm">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed" style={{wordBreak:"break-word"}}>
                      {getFuValue(fuStatus, fuDay, fuAction)
                        ? formatWhatsAppText(renderSample(getFuValue(fuStatus, fuDay, fuAction)))
                        : <span className="text-gray-400 italic">Empty template — start typing on the left to see preview</span>
                      }
                    </p>
                    <p className="text-[10px] text-gray-500 text-right mt-1 flex items-center justify-end gap-1">
                      14:32 <CheckCircle2 className="w-3 h-3 text-[#34B7F1]" />
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-gray-400 mt-1.5 leading-snug">
                Preview uses sample values · {FU_PLACEHOLDERS.map(p => `${p.key} → ${p.sample}`).slice(0, 2).join(" · ")}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSaveFollowupTemplates} disabled={saving} className="flex-1 bg-[#25D366] hover:bg-[#1da851] text-white rounded-lg" data-testid="wa-save-followup-templates-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Save Follow-up Templates
            </Button>
            <Button onClick={() => { setSendPreviewSource("followup"); setSendPreviewOpen(true); }} variant="outline" className="border-[#25D366] text-[#0D0D5B] hover:bg-[#25D366]/10 rounded-lg" data-testid="wa-send-preview-fu-btn">
              <Send className="w-4 h-4 mr-2" /> Send Preview to My Number
            </Button>
          </div>

          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800 flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            <span><strong>Voice & tone:</strong> Each message reads like a CCO sending a quick recap right after a phone call — warm, conversational, and ends with a clear next step. For Reschedule, the CCO can override the template per-lead from the follow-up dialog.</span>
          </div>
        </div>
      )}


      {activeTab === "templates" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">Customize the WhatsApp message sent to leads.</p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const def = (meetingDefaults && meetingDefaults[editLang]) || {};
                  if (!def.greeting) { toast.error("Defaults not loaded"); return; }
                  setTemplates(prev => ({ ...prev, [editLang]: { ...def } }));
                  toast.success(`Reset ${editLang === "en" ? "English" : "Bahasa Melayu"} meeting template to default`);
                }}
                className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-[#25D366]"
                data-testid="wa-meeting-reset-btn"
              >
                <RotateCcw className="w-3 h-3" /> Reset to default
              </button>
              <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg">
                {[{id:"en",label:"English"},{id:"ms",label:"Bahasa Melayu"}].map(l => (
                  <button key={l.id} onClick={() => setEditLang(l.id)} className={`py-1 px-3 text-xs font-medium rounded-md transition-colors ${editLang === l.id ? "bg-white text-[#25D366] shadow-sm" : "text-gray-500 hover:text-gray-700"}`} data-testid={`wa-lang-${l.id}`}>
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-xs text-gray-500">Greeting</Label>
              <Input value={tpl.greeting || ""} onChange={(e) => updateField("greeting", e.target.value)} className="border-gray-300 rounded-lg text-sm" placeholder="Hi {lead_name}," data-testid="wa-tpl-greeting" />
              <p className="text-[10px] text-gray-400 mt-0.5">Use {"{lead_name}"} and {"{workshop_name}"} as placeholders</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Meeting Details Message</Label>
              <textarea value={tpl.body || ""} onChange={(e) => updateField("body", e.target.value)} rows={3} className="w-full border border-gray-300 rounded-lg text-sm p-2.5 resize-none focus:ring-1 focus:ring-[#25D366] focus:border-[#25D366] outline-none" data-testid="wa-tpl-body" />
              <p className="text-[10px] text-gray-400 mt-0.5">Use {"{meeting_date}"}, {"{meeting_time}"} — wrap text with * for bold in WhatsApp</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Meeting Link Message</Label>
              <textarea value={tpl.meeting_link || ""} onChange={(e) => updateField("meeting_link", e.target.value)} rows={2} className="w-full border border-gray-300 rounded-lg text-sm p-2.5 resize-none focus:ring-1 focus:ring-[#25D366] focus:border-[#25D366] outline-none" data-testid="wa-tpl-link" />
              <p className="text-[10px] text-gray-400 mt-0.5">Use {"{join_url}"} to include the Teams meeting link</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Closing Message</Label>
              <textarea value={tpl.closing || ""} onChange={(e) => updateField("closing", e.target.value)} rows={2} className="w-full border border-gray-300 rounded-lg text-sm p-2.5 resize-none focus:ring-1 focus:ring-[#25D366] focus:border-[#25D366] outline-none" data-testid="wa-tpl-closing" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Sign Off</Label>
              <textarea value={tpl.sign_off || ""} onChange={(e) => updateField("sign_off", e.target.value)} rows={2} className="w-full border border-gray-300 rounded-lg text-sm p-2.5 resize-none focus:ring-1 focus:ring-[#25D366] focus:border-[#25D366] outline-none" data-testid="wa-tpl-signoff" />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSaveTemplates} disabled={saving} className="flex-1 bg-[#25D366] hover:bg-[#1da851] text-white rounded-lg" data-testid="wa-save-templates-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Save Templates
            </Button>
            <Button onClick={() => { setSendPreviewSource("meeting"); setSendPreviewOpen(true); }} variant="outline" className="border-[#25D366] text-[#0D0D5B] hover:bg-[#25D366]/10 rounded-lg" data-testid="wa-send-preview-meeting-btn">
              <Send className="w-4 h-4 mr-2" /> Send Preview
            </Button>
          </div>
        </div>
      )}

      {activeTab === "preview" && (
        <div className="space-y-4" data-testid="wa-preview-tab">
          {/* Top controls */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span>Preview as a real WhatsApp message with sample lead data.</span>
            </div>
            <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg">
              {[{id:"en",label:"EN"},{id:"ms",label:"BM"}].map(l => (
                <button key={l.id} onClick={() => { setEditLang(l.id); setFuLang(l.id); }} className={`py-1 px-3 text-xs font-semibold rounded-md transition-colors ${editLang === l.id ? "bg-white text-[#25D366] shadow-sm" : "text-gray-500 hover:text-gray-700"}`} data-testid={`wa-preview-lang-${l.id}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          {/* Mode toggle: Meeting / Follow-up / Registration / Unsubscribed */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
            {[
              {id:"registration",label:"Registration",icon:UserPlus2 || MessageSquare},
              {id:"meeting",label:"Meeting",icon:CalIcon},
              {id:"followup",label:"Follow-up",icon:MessageSquare},
              {id:"subscribed",label:"Subscribed",icon:MessageSquare},
              {id:"unsubscribed",label:"Unsubscribed",icon:MessageSquare},
            ].map(m => (
              <button key={m.id} onClick={() => setPreviewMode(m.id)} className={`flex-1 py-2 px-2 text-[11px] font-semibold rounded-md flex items-center justify-center gap-1 transition-colors ${previewMode === m.id ? "bg-white text-[#0D0D5B] shadow-sm" : "text-gray-500 hover:text-gray-700"}`} data-testid={`preview-mode-${m.id}`}>
                {m.label}
              </button>
            ))}
          </div>

          {/* Followup variant pickers (only in followup mode) */}
          {previewMode === "followup" && (
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold">Lead Status</Label>
                <select value={fuStatus} onChange={(e) => setFuStatus(e.target.value)} className="w-full mt-1 border border-gray-300 rounded-lg text-xs py-1.5 px-2 bg-white" data-testid="preview-fu-status">
                  {FU_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold">Day</Label>
                <select value={fuDay} onChange={(e) => setFuDay(Number(e.target.value))} className="w-full mt-1 border border-gray-300 rounded-lg text-xs py-1.5 px-2 bg-white" data-testid="preview-fu-day">
                  {FU_DAYS.map(d => <option key={d} value={d}>+{d} day</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold">Action</Label>
                <select value={fuAction} onChange={(e) => setFuAction(e.target.value)} className="w-full mt-1 border border-gray-300 rounded-lg text-xs py-1.5 px-2 bg-white" data-testid="preview-fu-action">
                  {FU_ACTIONS.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Phone mock-up frame */}
          <div className="mx-auto max-w-md">
            <div className="rounded-[28px] border-8 border-gray-900 bg-gray-900 shadow-2xl overflow-hidden" data-testid="wa-preview-phone">
              {/* WA header */}
              <div className="bg-[#075E54] text-white px-4 py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-sm font-bold">A</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">Ahmad · AutoCare Sdn Bhd</p>
                  <p className="text-[10px] text-white/70">online</p>
                </div>
                <Phone className="w-4 h-4 text-white/80" />
              </div>
              {/* Chat */}
              <div className="bg-[#E5DDD5] p-4 min-h-[280px] relative overflow-hidden">
                <div className="absolute inset-0 opacity-30 pointer-events-none" style={{backgroundImage:'radial-gradient(circle, #c5b9a8 1px, transparent 1px)', backgroundSize:'16px 16px'}}/>
                <div className="relative space-y-2">
                  <div className="max-w-[90%] ml-auto">
                    <div className="bg-[#DCF8C6] rounded-lg rounded-tr-none p-3 shadow-sm">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed" style={{wordBreak:"break-word"}}>
                        {previewMode === "meeting" ? (
                          previewMsg ? formatWhatsAppText(previewMsg) : <span className="text-gray-400 italic">No meeting template configured. Go to Meeting tab.</span>
                        ) : previewMode === "registration" ? (
                          (() => {
                            const text = getRegValue(editLang);
                            return text ? formatWhatsAppText(renderSample(text, "registration")) : <span className="text-gray-400 italic">No registration template — go to Registration tab.</span>;
                          })()
                        ) : previewMode === "unsubscribed" ? (
                          (() => {
                            const text = getUnsubValue(editLang);
                            return text ? formatWhatsAppText(renderSample(text, "unsubscribed")) : <span className="text-gray-400 italic">No unsubscribed template — go to Unsubscribed tab.</span>;
                          })()
                        ) : previewMode === "subscribed" ? (
                          (() => {
                            const text = getSubValue(editLang);
                            return text ? formatWhatsAppText(renderSample(text, "subscribed")) : <span className="text-gray-400 italic">No subscribed template — go to Subscribed tab.</span>;
                          })()
                        ) : (
                          (() => {
                            const text = getFuValue(fuStatus, fuDay, fuAction);
                            return text
                              ? formatWhatsAppText(renderSample(text))
                              : <span className="text-gray-400 italic">No template for this combination — go to Follow-up tab.</span>;
                          })()
                        )}
                      </p>
                      <p className="text-[10px] text-gray-500 text-right mt-1 flex items-center justify-end gap-1">
                        14:32 <CheckCircle2 className="w-3 h-3 text-[#34B7F1]" />
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* WA footer mock */}
              <div className="bg-[#F0F0F0] px-3 py-2 flex items-center gap-2">
                <div className="flex-1 bg-white rounded-full px-3 py-1.5 text-[10px] text-gray-400">Type a message</div>
                <div className="w-7 h-7 rounded-full bg-[#25D366] flex items-center justify-center"><MessageSquare className="w-3.5 h-3.5 text-white" /></div>
              </div>
            </div>
          </div>

          {/* Sample values panel */}
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Sample values used in preview</p>
              <button
                onClick={() => copyToClipboard(
                  previewMode === "meeting" ? previewMsg
                    : previewMode === "registration" ? renderSample(getRegValue(editLang), "registration")
                    : previewMode === "unsubscribed" ? renderSample(getUnsubValue(editLang), "unsubscribed")
                    : previewMode === "subscribed" ? renderSample(getSubValue(editLang), "subscribed")
                    : renderSample(getFuValue(fuStatus, fuDay, fuAction)),
                  "Rendered message copied"
                )}
                className="inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-[#25D366]"
                data-testid="preview-copy-rendered"
              >
                <Copy className="w-3 h-3" /> Copy rendered message
              </button>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {(previewMode === "meeting" ? MEETING_PLACEHOLDERS
                : previewMode === "registration" ? REGISTRATION_PLACEHOLDERS
                : previewMode === "unsubscribed" ? UNSUBSCRIBED_PLACEHOLDERS
                : previewMode === "subscribed" ? SUBSCRIBED_PLACEHOLDERS
                : FU_PLACEHOLDERS).map(p => (
                <div key={p.key} className="flex items-center justify-between gap-2 px-2 py-1 bg-white rounded border border-gray-200 text-[10.5px]">
                  <code className="text-[#0D0D5B] font-mono">{p.key}</code>
                  <span className="text-gray-500 truncate">{p.sample}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center">
            <Button onClick={() => { setSendPreviewSource(previewMode); setSendPreviewOpen(true); }} className="bg-[#25D366] hover:bg-[#1da851] text-white rounded-lg" data-testid="wa-send-preview-from-preview-btn">
              <Send className="w-4 h-4 mr-2" /> Send Preview to My Number
            </Button>
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>WhatsApp formatting: <code className="bg-white px-1 rounded">*bold*</code>, <code className="bg-white px-1 rounded">_italic_</code>, <code className="bg-white px-1 rounded">~strike~</code>. Each lead's <strong>preferred language</strong> determines which version is sent.</span>
          </div>
        </div>
      )}

      {/* Send Preview to My Number — shared dialog */}
      <Dialog open={sendPreviewOpen} onOpenChange={setSendPreviewOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="w-4 h-4 text-[#25D366]" /> Send Preview to My Number</DialogTitle>
            <DialogDescription>
              Fires the <strong>{({meeting:"Meeting", followup:"Follow-up", registration:"Registration", subscribed:"Subscribed", unsubscribed:"Unsubscribed"})[sendPreviewSource] || "Template"}</strong> template (<strong>{editLang.toUpperCase()}</strong>{sendPreviewSource === "followup" ? ` · ${fuStatus} · +${fuDay}d · ${fuAction}` : ""}) to your phone with sample values substituted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-gray-500">Your WhatsApp number (with country code)</Label>
              <Input
                value={sendPreviewPhone}
                onChange={(e) => setSendPreviewPhone(e.target.value)}
                placeholder="+60111234567"
                className="border-gray-300 rounded-lg font-mono text-sm"
                data-testid="wa-send-preview-phone"
                autoFocus
              />
              <p className="text-[10.5px] text-gray-400 mt-1">A "📨 PREVIEW" header is automatically prepended so the recipient knows it's a test.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendPreviewOpen(false)} data-testid="wa-send-preview-cancel">Cancel</Button>
            <Button onClick={handleSendPreview} disabled={sendingPreview || !sendPreviewPhone.trim()} className="bg-[#25D366] hover:bg-[#1da851] text-white" data-testid="wa-send-preview-confirm">
              {sendingPreview ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send Preview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
